import { Filesystem, Directory } from "@capacitor/filesystem";
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import { format } from "date-fns";

interface ReportData {
  transactions: any[];
  startDate: Date;
  endDate: Date;
  businessName: string;
  businessAddress?: string;
  businessPhone?: string;
  businessEmail?: string;
  businessLogo?: string | null;
  businessGstin?: string;
  productSummaries?: {
    name: string;
    quantity: number;
    value: number;
    unit: string;
  }[];
}

const PRIMARY_COLOR = [30, 41, 59]; // Slate 800
const SECONDARY_COLOR = [100, 116, 139]; // Slate 500
const BRAND_RED = [153, 27, 27]; // #991b1b
const BRAND_MUTED = [107, 114, 128]; // #6b7280 (muted-foreground)

const formatReportDate = (isoString: string | Date) => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "Invalid Date";
  return format(date, "dd-MM-yyyy HH:mm:ss");
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export const generatePDF = async (data: ReportData) => {
  const doc = new jsPDF();
  const {
    transactions,
    startDate,
    endDate,
    businessName,
    businessAddress,
    businessPhone,
    businessEmail,
    businessLogo,
    businessGstin,
  } = data;

  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Preload DailyHisaab logos
  let dhLogo: HTMLImageElement | null = null;
  let dhWatermark: HTMLImageElement | null = null;
  try {
    dhLogo = await loadImage("/DailyHisaab-logo-no-outline.png");
  } catch (e) {
    console.error("Failed to preload DailyHisaab header logo:", e);
  }
  try {
    dhWatermark = await loadImage("/DailyHisaab-logo-no-outline.png");
  } catch (e) {
    console.error("Failed to preload DailyHisaab watermark:", e);
  }

  // Background Header Stripe
  doc.setFillColor(PRIMARY_COLOR[0], PRIMARY_COLOR[1], PRIMARY_COLOR[2]);
  doc.rect(0, 0, pageWidth, 55, "F");

  // Business Logo (Left)
  let textX = 15;
  if (businessLogo) {
    try {
      const logoSize = 30;
      doc.addImage(businessLogo, "PNG", 15, 5, logoSize, logoSize);
      textX = 15 + logoSize + 5;
    } catch (e) {
      console.error("Failed to add business logo to PDF report:", e);
    }
  }

  // Business Name + Full Details (Left Side)
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(businessName.toUpperCase(), textX, 22);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  let leftY = 29;
  if (businessAddress) {
    doc.text(businessAddress, textX, leftY);
    leftY += 5;
  }
  if (businessGstin) {
    doc.text(`GSTIN: ${businessGstin}`, textX, leftY);
    leftY += 5;
  }
  let contactInfo = [];
  if (businessPhone) contactInfo.push(`Tel: ${businessPhone}`);
  if (businessEmail) contactInfo.push(`Email: ${businessEmail}`);
  if (contactInfo.length > 0) doc.text(contactInfo.join("  |  "), textX, leftY);

  // DailyHisaab Logo (Top Right only)
  const rightEdge = pageWidth - 10;
  if (dhLogo) {
    const aspectRatio = dhLogo.naturalWidth / dhLogo.naturalHeight;
    const imgH = 10;
    const imgW = imgH * aspectRatio;
    doc.addImage(dhLogo, "PNG", rightEdge - imgW, 10, imgW, imgH);
  } else {
    // Text fallback
    doc.setTextColor(BRAND_MUTED[0], BRAND_MUTED[1], BRAND_MUTED[2]);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text("Daily", rightEdge - 28, 17);
    doc.setTextColor(BRAND_RED[0], BRAND_RED[1], BRAND_RED[2]);
    doc.setFont("helvetica", "bold");
    doc.text("Hisaab", rightEdge - 14, 17);
  }

  // Report Title & Period
  yPos = 65;
  doc.setTextColor(PRIMARY_COLOR[0], PRIMARY_COLOR[1], PRIMARY_COLOR[2]);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("SALES & EXPENSES REPORT", 15, yPos);

  yPos += 8;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(SECONDARY_COLOR[0], SECONDARY_COLOR[1], SECONDARY_COLOR[2]);
  doc.text(
    `Period: ${format(startDate, "dd MMM yyyy")} to ${format(endDate, "dd MMM yyyy")}`,
    15,
    yPos,
  );

  yPos += 15;

  // Summary Cards
  const totalSales = transactions
    .filter((t) => t.type === "sale" || t.type === "SELL")
    .reduce((sum, t) => sum + Number(t.total || t.totalAmount || 0), 0);
  const totalExpenses = transactions
    .filter((t) => t.type === "expense" || t.type === "EXPENSE")
    .reduce((sum, t) => sum + Number(t.total || t.totalAmount || 0), 0);
  const netProfit = totalSales - Math.abs(totalExpenses);

  // Sales Card
  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(15, yPos, 55, 25, 2, 2, "FD");
  doc.setTextColor(SECONDARY_COLOR[0], SECONDARY_COLOR[1], SECONDARY_COLOR[2]);
  doc.setFontSize(8);
  doc.text("TOTAL SALES", 20, yPos + 8);
  doc.setTextColor(22, 163, 74); // Green 600
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(
    `Rs. ${totalSales.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
    20,
    yPos + 18,
  );

  // Expenses Card
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(77, yPos, 55, 25, 2, 2, "FD");
  doc.setTextColor(SECONDARY_COLOR[0], SECONDARY_COLOR[1], SECONDARY_COLOR[2]);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("TOTAL EXPENSES", 82, yPos + 8);
  doc.setTextColor(220, 38, 38); // Red 600
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(
    `Rs. ${Math.abs(totalExpenses).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
    82,
    yPos + 18,
  );

  // Net Card
  doc.setFillColor(PRIMARY_COLOR[0], PRIMARY_COLOR[1], PRIMARY_COLOR[2]);
  doc.roundedRect(140, yPos, 55, 25, 2, 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("NET BALANCE", 145, yPos + 8);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(
    `Rs. ${netProfit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
    145,
    yPos + 18,
  );

  yPos += 40;

  // Product Wise Sales (Pro Users Only)
  if (data.productSummaries && data.productSummaries.length > 0) {
    doc.setTextColor(PRIMARY_COLOR[0], PRIMARY_COLOR[1], PRIMARY_COLOR[2]);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("PRODUCT-WISE SALES SUMMARY", 15, yPos);
    yPos += 8;

    const productColumn = ["PRODUCT NAME", "QUANTITY", "TOTAL VALUE"];
    const sortedSummaries = [...data.productSummaries].sort(
      (a, b) => b.quantity - a.quantity,
    );
    const productRows = sortedSummaries.map((p) => [
      p.name,
      `${p.quantity} ${p.unit}`,
      `Rs. ${p.value.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [productColumn],
      body: productRows,
      theme: "striped",
      headStyles: {
        fillColor: SECONDARY_COLOR as any,
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: "bold",
      },
      styles: { fontSize: 8, cellPadding: 3 },
      margin: { left: 15, right: 15 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // Transactions Table
  const tableColumn = ["DATE & TIME", "TYPE", "ITEMS / DESCRIPTION", "AMOUNT"];
  const tableRows = transactions.map((t) => {
    const itemsStr = t.items
      ? t.items.map((i: any) => `${i.quantity}x ${i.productName}`).join(", ")
      : t.productName
        ? `${t.quantity}x ${t.productName}`
        : "General Transaction";

    return [
      formatReportDate(t.createdAt || t.timestamp || new Date()),
      (t.type || "").toUpperCase(),
      itemsStr,
      {
        content: `Rs. ${Math.abs(Number(t.total || t.totalAmount || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        styles: {
          textColor:
            t.type === "expense" || t.type === "EXPENSE"
              ? [220, 38, 38]
              : [22, 163, 74],
          fontStyle: "bold",
        },
      },
    ];
  });

  autoTable(doc, {
    startY: yPos,
    head: [tableColumn],
    body: tableRows,
    theme: "grid",
    headStyles: {
      fillColor: PRIMARY_COLOR as any,
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
    },
    styles: {
      fontSize: 8,
      cellPadding: 4,
      valign: "middle",
    },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 20, halign: "center" },
      2: { cellWidth: "auto" },
      3: { cellWidth: 40, halign: "right" },
    },
    alternateRowStyles: { fillColor: [249, 250, 251] },
  });

  // Footer + Watermark
  const pageCount = (doc as any).internal.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Watermark (centered, faint)
    if (dhWatermark) {
      const wmW = 100;
      const wmH = wmW / (dhWatermark.naturalWidth / dhWatermark.naturalHeight);
      const wmX = (pageWidth - wmW) / 2;
      const wmY = (pageHeight - wmH) / 2;
      (doc as any).saveGraphicsState();
      (doc as any).setGState(new (doc as any).GState({ opacity: 0.07 }));
      doc.addImage(dhWatermark, "PNG", wmX, wmY, wmW, wmH);
      (doc as any).restoreGraphicsState();
    }

    doc.setFontSize(8);
    doc.setTextColor(
      SECONDARY_COLOR[0],
      SECONDARY_COLOR[1],
      SECONDARY_COLOR[2],
    );
    doc.text(
      `Generated by DailyHisaab on ${format(new Date(), "dd-MM-yyyy HH:mm")}`,
      15,
      pageHeight - 10,
    );
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 25, pageHeight - 10);
  }

  const fileName = `DailyHisaab_Report_${format(startDate, "yyyy-MM-dd")}_to_${format(endDate, "yyyy-MM-dd")}.pdf`;

  if (Capacitor.isNativePlatform()) {
    try {
      let perm = await Filesystem.checkPermissions();
      if (perm.publicStorage !== 'granted') {
        perm = await Filesystem.requestPermissions();
      }

      if (perm.publicStorage !== 'granted') {
        throw new Error("Storage permission denied by user.");
      }

      const pdfBase64 = doc.output('datauristring').split(',')[1];
      await Filesystem.writeFile({
        path: fileName,
        data: pdfBase64,
        directory: Directory.Documents,
      });
    } catch (e: any) {
      console.error("Failed to save PDF natively:", e);
      throw e;
    }
  } else {
    doc.save(fileName);
  }
};

// ADDED ASYNC KEYWORD HERE
export const generateExcel = async (data: ReportData) => {
  const {
    transactions,
    startDate,
    endDate,
    businessName,
    businessAddress,
    businessPhone,
    businessEmail,
  } = data;

  const workbook = XLSX.utils.book_new();

  // Prepare Summary Sheet
  const totalSales = transactions
    .filter((t) => t.type === "sale" || t.type === "SELL")
    .reduce((sum, t) => sum + Number(t.total || t.totalAmount || 0), 0);
  const totalExpenses = transactions
    .filter((t) => t.type === "expense" || t.type === "EXPENSE")
    .reduce((sum, t) => sum + Number(t.total || t.totalAmount || 0), 0);

  const summaryData = [
    [
      {
        v: businessName.toUpperCase(),
        s: { font: { bold: true, sz: 16, color: { rgb: "1e293b" } } },
      },
    ],
    [
      {
        v: `${businessAddress || ""}`,
        s: { font: { sz: 10, color: { rgb: "64748b" } } },
      },
    ],
    [
      {
        v: `Tel: ${businessPhone || ""} | Email: ${businessEmail || ""}`,
        s: { font: { sz: 10, color: { rgb: "64748b" } } },
      },
    ],
    [],
    [
      {
        v: "SALES & EXPENSES REPORT",
        s: { font: { bold: true, sz: 14, color: { rgb: "991b1b" } } },
      },
    ],
    [
      {
        v: `Period: ${format(startDate, "dd-MM-yyyy")} to ${format(endDate, "dd-MM-yyyy")}`,
        s: { font: { sz: 10, color: { rgb: "64748b" } } },
      },
    ],
    [],
    [{ v: "SUMMARY", s: { font: { bold: true, sz: 12 } } }],
    [
      "Total Sales",
      {
        v: totalSales,
        t: "n",
        s: { font: { color: { rgb: "16a34a" }, bold: true } },
      },
    ],
    [
      "Total Expenses",
      {
        v: Math.abs(totalExpenses),
        t: "n",
        s: { font: { color: { rgb: "dc2626" }, bold: true } },
      },
    ],
    [
      "Net Balance",
      {
        v: totalSales - Math.abs(totalExpenses),
        t: "n",
        s: { font: { bold: true } },
      },
    ],
    [],
    [
      {
        v: "DailyHisaab Mobile Billing WebApp",
        s: { font: { italic: true, sz: 9, color: { rgb: "6b7280" } } },
      },
    ],
    [
      {
        v: `Report Generated on: ${format(new Date(), "dd-MM-yyyy HH:mm")}`,
        s: { font: { sz: 9, color: { rgb: "6b7280" } } },
      },
    ],
  ];

  // Add Product Summary if available
  if (data.productSummaries && data.productSummaries.length > 0) {
    summaryData.push([]);
    summaryData.push([
      { v: "PRODUCT-WISE SALES SUMMARY", s: { font: { bold: true, sz: 12 } } },
    ]);
    summaryData.push(["Product Name", "Quantity", "Total Value"]);
    const sortedSummaries = [...data.productSummaries].sort(
      (a, b) => b.quantity - a.quantity,
    );
    sortedSummaries.forEach((p) => {
      summaryData.push([
        p.name,
        `${p.quantity} ${p.unit}`,
        { v: p.value.toString(), t: "n" } as any,
      ]);
    });
  }

  // Prepare Transactions Sheet
  const reportData: any[] = [];
  transactions.forEach((tx) => {
    const items =
      tx.items ||
      (tx.productName
        ? [
          {
            productId: tx.productId,
            productName: tx.productName,
            quantity: tx.quantity,
            totalAmount: tx.total,
          },
        ]
        : [
          {
            productName: "General Transaction",
            quantity: 1,
            totalAmount: tx.total,
          },
        ]);

    items.forEach((item: any) => {
      const isExpense = tx.type === "expense" || tx.type === "EXPENSE";
      reportData.push([
        formatReportDate(tx.createdAt || tx.timestamp || new Date()),
        (tx.type || "").toUpperCase(),
        item.productName,
        item.quantity,
        {
          v: isExpense
            ? -Math.abs(item.totalAmount || tx.total)
            : Math.abs(item.totalAmount || tx.total),
          t: "n",
          s: {
            font: {
              color: { rgb: isExpense ? "dc2626" : "16a34a" },
              bold: true,
            },
          },
        },
      ]);
    });
  });

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

  // Transaction sheet with custom headers
  const transHeader = [
    "Date & Time",
    "Type",
    "Product Name",
    "Quantity",
    "Amount (Rs)",
  ];
  const transAoa = [
    transHeader.map((h) => ({
      v: h,
      s: {
        fill: { fgColor: { rgb: "1e293b" } },
        font: { color: { rgb: "ffffff" }, bold: true },
      },
    })),
    ...reportData,
  ];
  const transactionSheet = XLSX.utils.aoa_to_sheet(transAoa);

  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
  XLSX.utils.book_append_sheet(workbook, transactionSheet, "Transactions");

  // Adjust column widths
  transactionSheet["!cols"] = [
    { wch: 22 }, // Date
    { wch: 12 }, // Type
    { wch: 35 }, // Product Name
    { wch: 10 }, // Quantity
    { wch: 18 }, // Amount
  ];

  const fileName = `DailyHisaab_Report_${format(startDate, "yyyy-MM-dd")}_to_${format(endDate, "yyyy-MM-dd")}.xlsx`;

  if (Capacitor.isNativePlatform()) {
    try {
      let perm = await Filesystem.checkPermissions();
      if (perm.publicStorage !== 'granted') {
        perm = await Filesystem.requestPermissions();
      }

      if (perm.publicStorage !== 'granted') {
        throw new Error("Storage permission denied by user.");
      }

      const excelBase64 = XLSX.write(workbook, { bookType: 'xlsx', type: 'base64' });
      await Filesystem.writeFile({
        path: fileName,
        data: excelBase64,
        directory: Directory.Documents,
      });
    } catch (e: any) {
      console.error("Failed to save Excel natively:", e);
      throw e;
    }
  } else {
    XLSX.writeFile(workbook, fileName);
  }
};